import {
  AddLocationRequest,
  AddLocationResponse,
  CatalogApi,
  CatalogRequestOptions,
  GetEntitiesByRefsRequest,
  GetEntitiesByRefsResponse,
  GetEntitiesRequest,
  GetEntitiesResponse,
  GetEntityAncestorsRequest,
  GetEntityAncestorsResponse,
  GetEntityFacetsRequest,
  GetEntityFacetsResponse,
  GetLocationsResponse,
  Location,
  QueryEntitiesRequest,
  QueryEntitiesResponse,
  StreamEntitiesRequest,
  ValidateEntityResponse,
} from '@backstage/catalog-client';
import {
  AnalyzeLocationRequest,
  AnalyzeLocationResponse,
} from '@backstage/plugin-catalog-common';
import {
  CompoundEntityRef,
  Entity,
  parseEntityRef,
} from '@backstage/catalog-model';
import { FusionCatalogService } from './fusionCatalogService';
import { Config } from '@backstage/config';

/**
 * An implementation of the Catalog API that delegates to the catalog client and the Fusion service.
 * This service will be used from the scaffolder backend and catalog API frontend to fetch data from the Fusion API.
 */
export class CatalogService implements CatalogApi {
  private _catalogClient: CatalogApi;
  // @ts-ignore
  private _fusionService: FusionCatalogService;

  constructor(catalogClient: CatalogApi, fusionService: FusionCatalogService) {
    this._catalogClient = catalogClient;
    this._fusionService = fusionService;
  }

  /**
   * @inheritDoc
   */
  async getLocationByEntity(
    entityRef: string | CompoundEntityRef,
    options?: CatalogRequestOptions | undefined,
  ): Promise<Location | undefined> {
    return await this._catalogClient.getLocationByEntity(entityRef, options);
  }

  /**
   * Retrieves an entity by its name.
   * @param compoundName The compound name of the entity.
   * @param options The options for the request.
   * @deprecated Use getEntityByRef instead
   */
  async getEntityByName(
    compoundName: CompoundEntityRef,
    options?: CatalogRequestOptions,
  ): Promise<Entity | undefined> {
    return this.getEntityByRef(compoundName, options);
  }

  /**
   * Retrieves entities from the catalog.
   *
   * @param request - The request containing criteria for entity retrieval.
   * @param options - The options for the catalog request.
   * @returns - A promise that resolves with the response containing the retrieved entities.
   */
  async getEntities(
    request?: GetEntitiesRequest,
    options?: CatalogRequestOptions,
  ): Promise<GetEntitiesResponse> {
    return this._catalogClient.getEntities(request, options);
  }

  /**
   * Retrieves entities by their references.
   *
   * @param request - The request object containing the entity references.
   * @param [options] - The optional request options.
   * @returns - A promise that resolves with the response containing the retrieved entities.
   */
  public async getEntitiesByRefs(
    request: GetEntitiesByRefsRequest,
    options?: CatalogRequestOptions,
  ): Promise<GetEntitiesByRefsResponse> {
    const res = await this._catalogClient.getEntitiesByRefs(request, options);

    const systemIds: string[] = [];
    for (const ref of request.entityRefs) {
      const reference = parseEntityRef(ref);
      if (reference.kind.toLowerCase() === 'system') {
        systemIds.push(reference.name);
      }
    }
    const systems: (Entity | undefined)[] = systemIds.length
      ? await this._fusionService.findSystemsForApplicationIds(systemIds)
      : [];

    if (systems.length > 0) {
      res.items = [...res.items, ...systems];
    }

    return res;
  }

  /**
   * Queries entities asynchronously.
   *
   * @param request - The query entities request.
   * @param options - The catalog request options.
   * @returns - A promise that resolves to the query entities response.
   */
  public async queryEntities(
    request?: QueryEntitiesRequest,
    options?: CatalogRequestOptions,
  ): Promise<QueryEntitiesResponse> {
    return await this._catalogClient.queryEntities(request, options);
  }

  /**
   * Retrieves the ancestors of an entity.
   *
   * @param request - The request object that specifies the entity to retrieve the ancestors for.
   * @param [options] - The optional request options.
   *
   * @returns - A promise that resolves to the response object containing the ancestors of the entity.
   */
  public async getEntityAncestors(
    request: GetEntityAncestorsRequest,
    options?: CatalogRequestOptions,
  ): Promise<GetEntityAncestorsResponse> {
    return await this._catalogClient.getEntityAncestors(request, options);
  }

  /**
   * Retrieves an entity by reference.
   *
   * @param entityRef - The reference to the entity.
   * @param [options] - The optional request options.
   * @returns - A promise that resolves to the found entity or undefined if not found.
   */
  public async getEntityByRef(
    entityRef: string | CompoundEntityRef,
    options?: CatalogRequestOptions,
  ): Promise<Entity | undefined> {
    let reference = entityRef;
    if (typeof entityRef === 'string') {
      reference = parseEntityRef(entityRef);
    }
    if (!(typeof reference === 'object')) {
      return await this._catalogClient.getEntityByRef(entityRef, options);
    }
    const entityKind = reference.kind.toLowerCase();
    if (entityKind === 'system') {
      const applicationId = reference.name.replace(/[^0-9]+/, '');
      return await this._fusionService.findSystemForApplicationId(
        applicationId,
      );
    }
    return await this._catalogClient.getEntityByRef(entityRef, options);
  }

  /**
   * Removes an entity from the catalog by its UID.
   *
   * @param uid - The UID of the entity to be removed.
   * @param [options] - The additional options for the removal request.
   * @returns - A promise that resolves when the entity is successfully removed.
   */
  public async removeEntityByUid(
    uid: string,
    options?: CatalogRequestOptions,
  ): Promise<void> {
    return await this._catalogClient.removeEntityByUid(uid, options);
  }

  /**
   * Refreshes an entity.
   *
   * @param entityRef - The reference of the entity to refresh.
   * @param [options] - The options for the request.
   * @returns A promise that resolves with void.
   */
  public async refreshEntity(
    entityRef: string,
    options?: CatalogRequestOptions,
  ): Promise<void> {
    return await this._catalogClient.refreshEntity(entityRef, options);
  }

  /**
   * Retrieves the facets associated with an entity.
   *
   * @param request - The request object containing the necessary parameters.
   * @param [options] - The optional request options.
   * @returns The promise that resolves to the entity facets response.
   */
  public async getEntityFacets(
    request: GetEntityFacetsRequest,
    options?: CatalogRequestOptions,
  ): Promise<GetEntityFacetsResponse> {
    return await this._catalogClient.getEntityFacets(request, options);
  }

  /**
   * Retrieves a location by its ID.
   *
   * @param id - The ID of the location to retrieve.
   * @param [options] - Additional options for the request.
   * @returns - A Promise that resolves to the retrieved location, or `undefined` if not found.
   */
  public async getLocationById(
    id: string,
    options?: CatalogRequestOptions,
  ): Promise<Location | undefined> {
    return (await this._catalogClient.getLocationById(id, options)) as
      | Location
      | undefined;
  }

  /**
   * Get the location by reference.
   *
   * @param locationRef - The reference of the location.
   * @param [options] - The optional request options.
   * @returns A promise that resolves with the location object or undefined.
   */
  public async getLocationByRef(
    locationRef: string,
    options?: CatalogRequestOptions,
  ): Promise<Location | undefined> {
    return (await this._catalogClient.getLocationByRef(
      locationRef,
      options,
    )) as Location | undefined;
  }

  /**
   * Adds a location to the catalog.
   *
   * @param location - The location to be added.
   * @param [options] - The optional request options.
   * @returns A promise that resolves with the response from adding the location.
   */
  public async addLocation(
    location: AddLocationRequest,
    options?: CatalogRequestOptions,
  ): Promise<AddLocationResponse> {
    return await this._catalogClient.addLocation(location, options);
  }

  /**
   * Removes a location by its ID.
   *
   * @param id - The ID of the location to remove.
   * @param [options] - Optional request options.
   * @returns - A promise that resolves when the location is successfully removed.
   */
  public async removeLocationById(
    id: string,
    options?: CatalogRequestOptions,
  ): Promise<void> {
    return await this._catalogClient.removeLocationById(id, options);
  }

  /**
   * Validates an entity.
   *
   * @param entity - The entity to be validated.
   * @param locationRef - The location reference of the entity.
   * @param [options] - Additional options for the validation request.
   * @returns A promise that resolves with the response of the validation.
   */
  public async validateEntity(
    entity: Entity,
    locationRef: string,
    options?: CatalogRequestOptions,
  ): Promise<ValidateEntityResponse> {
    return await this._catalogClient.validateEntity(
      entity,
      locationRef,
      options,
    );
  }

  /**
   * Gets catalog locations.
   *
   * @param [request] - Request parameters.
   * @param [options] - Additional options for the request.
   * @returns A promise that resolves with the locations response.
   */
  public async getLocations(
    request?: {},
    options?: CatalogRequestOptions,
  ): Promise<GetLocationsResponse> {
    return await this._catalogClient.getLocations(request, options);
  }

  /**
   * Analyzes a location.
   *
   * @param location - The location to analyze.
   * @param [options] - Additional options for the request.
   * @returns A promise that resolves with the analysis response.
   */
  public async analyzeLocation(
    location: AnalyzeLocationRequest,
    options?: CatalogRequestOptions,
  ): Promise<AnalyzeLocationResponse> {
    return await this._catalogClient.analyzeLocation(location, options);
  }

  /**
   * Streams entities from the catalog.
   *
   * @param [request] - Request parameters for streaming.
   * @param [options] - Additional options for the request.
   * @returns An async iterable of entity arrays.
   */
  public streamEntities(
    request?: StreamEntitiesRequest,
    options?: CatalogRequestOptions,
  ): AsyncIterable<Entity[]> {
    return this._catalogClient.streamEntities(request, options);
  }
}

/**
 * A factory for creating a Catalog service.
 */
export class CatalogServiceFactory {
  private static config: Config;
  private static isDevHubLite: boolean;

  /**
   * Initializes the Catalog service factory with the given configuration.
   * @param config The configuration
   */
  public static init(config: Config): void {
    if (!CatalogServiceFactory.config) {
      CatalogServiceFactory.config = config;

      CatalogServiceFactory.isDevHubLite =
        config.getString('app.version') === 'devhub-lite';
    }
  }

  /**
   * Creates a Catalog service with the given client and Fusion service.
   * @param catalogClient The catalog client
   * @param fusionCatalogFactory The Fusion catalog factory
   */
  public static async createAsync(
    catalogClient: CatalogApi,
    fusionCatalogFactory: () => Promise<FusionCatalogService>,
  ): Promise<CatalogApi> {
    if (!CatalogServiceFactory.config) {
      throw new Error('CatalogServiceFactory not initialized');
    }
    if (fusionCatalogFactory && !CatalogServiceFactory.isDevHubLite) {
      return new CatalogService(catalogClient, await fusionCatalogFactory());
    }
    return catalogClient;
  }

  /**
   * Creates a Catalog service with the given client and Fusion service.
   * @param catalogClient The catalog client
   * @param fusionCatalogFactory The Fusion catalog factory
   */
  public static create(
    catalogClient: CatalogApi,
    fusionCatalogFactory: () => FusionCatalogService,
  ): CatalogApi {
    if (!CatalogServiceFactory.config) {
      throw new Error('CatalogServiceFactory not initialized');
    }
    if (fusionCatalogFactory && !CatalogServiceFactory.isDevHubLite) {
      return new CatalogService(catalogClient, fusionCatalogFactory());
    }
    return catalogClient;
  }
}
